import type {
  AuthResponse,
  BalanceBundle,
  BootstrapResponse,
  CollectionResponse,
  CreateGuild,
  CreateGuildResponse,
  ErrorResponse,
  NameCheckResponse,
  UploadLogoResponse,
} from '@guildrise/shared';
import {
  type ArenaCommand,
  type ArenaHistoryEntry,
  ArenaHistoryResponseSchema,
  ArenaResponseSchema,
  type ArenaView,
  CollectionResponseSchema,
} from '@guildrise/shared';

/**
 * Запросы к игровому API.
 *
 * Клиент не содержит логики принятия решений: отправляет намерение,
 * отображает ответ.
 */

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export class ApiError extends Error {
  readonly code: string;
  /** Контекст от сервера, например свободный номер при NAME_TAKEN. */
  readonly details: Readonly<Record<string, unknown>> | undefined;
  readonly serverNow: number | undefined;
  readonly retryAfterSeconds: number | undefined;

  constructor(
    code: string,
    message: string,
    details?: Readonly<Record<string, unknown>>,
    serverNow?: number,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
    this.serverNow = serverNow;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

async function parseError(response: Response): Promise<ApiError> {
  const retryHeader = response.headers.get('Retry-After');
  const seconds = retryHeader === null ? NaN : Number(retryHeader);
  const retryDate = retryHeader === null ? NaN : Date.parse(retryHeader);
  const responseDate = Date.parse(response.headers.get('Date') ?? '');
  const headerDelay =
    Number.isFinite(seconds) && seconds >= 0
      ? seconds
      : Number.isFinite(retryDate) && Number.isFinite(responseDate)
        ? Math.max(0, (retryDate - responseDate) / 1000)
        : undefined;
  try {
    const body = (await response.json()) as ErrorResponse & { serverNow?: unknown };
    const serverNow =
      typeof body.serverNow === 'number' &&
      Number.isSafeInteger(body.serverNow) &&
      body.serverNow >= 0
        ? body.serverNow
        : undefined;
    const detailDelay = body.error.details?.retryAfterSeconds;
    const retryDelay =
      headerDelay ??
      (typeof detailDelay === 'number' && Number.isFinite(detailDelay) && detailDelay >= 0
        ? detailDelay
        : undefined);
    return new ApiError(
      response.status === 429 ? 'RATE_LIMITED' : body.error.code,
      body.error.message,
      body.error.details,
      serverNow,
      retryDelay,
    );
  } catch {
    return new ApiError(
      response.status === 429 ? 'RATE_LIMITED' : 'INTERNAL',
      'Сервер недоступен',
      undefined,
      undefined,
      headerDelay,
    );
  }
}

/**
 * Вход по initData.
 *
 * Строка уходит на сервер как есть, без разбора на клиенте: разбирать
 * её здесь бессмысленно — доверять можно только проверенной подписи.
 */
export async function authenticate(initData: string, accessKey?: string): Promise<AuthResponse> {
  const response = await fetch(`${API_URL}/v1/auth/telegram`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Каждый POST несёт ключ идемпотентности: сеть в Telegram рвётся постоянно.
      'Idempotency-Key': crypto.randomUUID(),
      'X-Client-Version': __APP_VERSION__,
    },
    body: JSON.stringify({ initData, accessKey }),
  });

  if (!response.ok) throw await parseError(response);
  return (await response.json()) as AuthResponse;
}

/**
 * Пакет баланса.
 *
 * GET безопасно повторять, поэтому при сетевом сбое делаем до трёх
 * попыток с нарастающей паузой — в отличие от POST, где повтор решает
 * только игрок.
 */
export async function fetchBalance(): Promise<BalanceBundle> {
  let lastError: unknown;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const response = await fetch(`${API_URL}/v1/balance`, {
        headers: { 'X-Client-Version': __APP_VERSION__ },
      });
      if (!response.ok) throw await parseError(response);
      return (await response.json()) as BalanceBundle;
    } catch (error) {
      lastError = error;
      if (attempt < 2) {
        await new Promise((resolve) => setTimeout(resolve, 300 * 2 ** attempt));
      }
    }
  }

  throw lastError instanceof ApiError ? lastError : new ApiError('INTERNAL', 'Баланс недоступен');
}

/** Токен входа хранится в памяти вкладки: localStorage читают расширения. */
let accessToken: string | null = null;
let authRevision = 0;
const sessionListeners = new Set<() => void>();
const authFailureListeners = new Set<(error: ApiError) => void>();

/** Версия сессии, без раскрытия токена: для сброса приватных данных и поздних ответов. */
export const getAuthRevision = (): number => authRevision;

export function subscribeAuthSession(listener: () => void): () => void {
  sessionListeners.add(listener);
  return () => {
    sessionListeners.delete(listener);
  };
}

export function subscribeAuthFailure(listener: (error: ApiError) => void): () => void {
  authFailureListeners.add(listener);
  return () => {
    authFailureListeners.delete(listener);
  };
}

export function setAccessToken(token: string | null): void {
  if (accessToken === token) return;
  accessToken = token;
  authRevision += 1;
  for (const listener of sessionListeners) listener();
}

function authHeaders(): Record<string, string> {
  return accessToken === null ? {} : { Authorization: `Bearer ${accessToken}` };
}

/** Коллекция гильдии: проверяем ответ до отображения карт. */
export async function fetchCollection(signal?: AbortSignal): Promise<CollectionResponse> {
  const revision = authRevision;
  const assertCurrent = () => {
    signal?.throwIfAborted();
    if (revision !== authRevision) throw new DOMException('Сессия изменилась', 'AbortError');
  };
  assertCurrent();
  const response = await fetch(`${API_URL}/v1/arsenal/characters`, {
    cache: 'no-store',
    headers: { 'X-Client-Version': __APP_VERSION__, ...authHeaders() },
    ...(signal === undefined ? {} : { signal }),
  });
  assertCurrent();
  if (!response.ok) {
    const parsed = await parseError(response);
    assertCurrent();
    // HTTP 401 остаётся отказом авторизации даже с пустым/не-JSON телом.
    const error =
      response.status === 401
        ? new ApiError('UNAUTHORIZED', 'Сессия истекла. Войди снова.')
        : parsed;
    if (response.status === 401 || error.code === 'CLOSED_ACCESS_REQUIRED') {
      setAccessToken(null);
      for (const listener of authFailureListeners) listener(error);
    }
    throw error;
  }
  const collection = CollectionResponseSchema.parse(await response.json());
  assertCurrent();
  return collection;
}

/**
 * Состояние игрока одним запросом: шаг создания, гильдия, версия баланса.
 */
export async function fetchBootstrap(): Promise<BootstrapResponse> {
  const response = await fetch(`${API_URL}/v1/bootstrap`, {
    headers: { 'X-Client-Version': __APP_VERSION__, ...authHeaders() },
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as BootstrapResponse;
}

/** Arena requests use the same session guard as the collection, including late responses. */
export async function fetchArenaHistory(signal?: AbortSignal): Promise<ArenaHistoryEntry[]> {
  const revision = authRevision;
  const combined = signal
    ? AbortSignal.any([signal, AbortSignal.timeout(8000)])
    : AbortSignal.timeout(8000);
  const check = () => {
    combined.throwIfAborted();
    if (revision !== authRevision) throw new DOMException('Сессия изменилась', 'AbortError');
  };
  check();
  const response = await fetch(`${API_URL}/v1/arena/history`, {
    cache: 'no-store',
    headers: authHeaders(),
    signal: combined,
  });
  check();
  if (!response.ok) {
    const parsed = await parseError(response);
    check();
    const error =
      response.status === 401
        ? new ApiError('UNAUTHORIZED', 'Сессия истекла. Войди снова.')
        : parsed;
    if (response.status === 401 || error.code === 'CLOSED_ACCESS_REQUIRED') {
      setAccessToken(null);
      for (const listener of authFailureListeners) listener(error);
    }
    throw error;
  }
  const body = ArenaHistoryResponseSchema.parse(await response.json());
  check();
  return body.history;
}

export async function arenaRequest(
  operation: 'current' | 'start' | 'move',
  body?: { cards: string[] } | { matchId: string; revision: number; command: ArenaCommand },
  key?: string,
  signal?: AbortSignal,
): Promise<ArenaView | null> {
  signal = signal
    ? AbortSignal.any([signal, AbortSignal.timeout(8000)])
    : AbortSignal.timeout(8000);
  const revision = authRevision;
  const assertCurrent = () => {
    signal?.throwIfAborted();
    if (revision !== authRevision) throw new DOMException('Сессия изменилась', 'AbortError');
  };
  assertCurrent();
  const response = await fetch(`${API_URL}/v1/arena/${operation}`, {
    method: operation === 'current' ? 'GET' : 'POST',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(key ? { 'Idempotency-Key': key } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal === undefined ? {} : { signal }),
  });
  assertCurrent();
  if (!response.ok) {
    const parsed = await parseError(response);
    assertCurrent();
    const error =
      response.status === 401
        ? new ApiError('UNAUTHORIZED', 'Сессия истекла. Войди снова.')
        : parsed;
    if (response.status === 401 || error.code === 'CLOSED_ACCESS_REQUIRED') {
      setAccessToken(null);
      for (const listener of authFailureListeners) listener(error);
    }
    throw error;
  }
  const result = ArenaResponseSchema.parse(await response.json());
  assertCurrent();
  return result.match;
}

/**
 * Свободна ли пара «имя#номер». Если занято — подсказка со свободным номером.
 *
 * GET без повторов: игрок набирает имя, и повторять неудачную
 * проверку бессмысленно — следующая буква вызовет свою.
 */
export async function checkGuildName(
  name: string,
  discriminator: string,
): Promise<NameCheckResponse> {
  const params = new URLSearchParams({ name, discriminator });
  const response = await fetch(`${API_URL}/v1/guild/name-check?${params.toString()}`, {
    headers: { 'X-Client-Version': __APP_VERSION__, ...authHeaders() },
  });

  if (!response.ok) throw await parseError(response);
  return (await response.json()) as NameCheckResponse;
}

/**
 * Создание гильдии.
 *
 * Ключ идемпотентности создаётся снаружи и переживает повтор: иначе
 * новый ключ на каждую попытку создал бы вторую гильдию.
 */
export async function createGuild(
  input: CreateGuild,
  idempotencyKey: string,
): Promise<CreateGuildResponse> {
  const response = await fetch(`${API_URL}/v1/guild/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
      'X-Client-Version': __APP_VERSION__,
      ...authHeaders(),
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) throw await parseError(response);
  return (await response.json()) as CreateGuildResponse;
}

/** Загрузка логотипа. Картинка уже сжата клиентом до WebP 256×256. */
export async function uploadLogo(blob: Blob, idempotencyKey: string): Promise<UploadLogoResponse> {
  const response = await fetch(`${API_URL}/v1/guild/logo/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'image/webp',
      'Idempotency-Key': idempotencyKey,
      'X-Client-Version': __APP_VERSION__,
      ...authHeaders(),
    },
    body: blob,
  });

  if (!response.ok) throw await parseError(response);
  return (await response.json()) as UploadLogoResponse;
}
